import {
  isCompanyId,
  isNotDefined,
  isRoleId,
  isTeamId,
  pickHighestPermissionScope,
  type CompanyId,
  type DashboardScopeId,
  type PermissionMap,
  type TeamData,
  type TeamId,
  type UserId,
} from '@terros/common'

/**
 * How far a viewer's `dashboard:manage` permission reaches, which decides the
 * audiences they may build a dashboard for. The server enforces this again.
 */
export type DashboardManageTier = 'selfOnly' | 'teams' | 'company'

export const DASHBOARD_AUDIENCE_KINDS = ['yourself', 'team', 'company', 'role', 'person'] as const
export type DashboardAudienceKind = (typeof DASHBOARD_AUDIENCE_KINDS)[number]

export function isDashboardAudienceKind(value: string): value is DashboardAudienceKind {
  return DASHBOARD_AUDIENCE_KINDS.some((kind) => kind === value)
}

export type DashboardAudienceTarget = {
  entityId: DashboardScopeId
  name: string
}

export type DashboardAudience = {
  kind: DashboardAudienceKind
  /** Absent for `yourself`, and for a kind whose sub-selection is unresolved. */
  target?: DashboardAudienceTarget
}

export function getDashboardManageTier(permissionMap: PermissionMap | undefined): DashboardManageTier {
  const scope = pickHighestPermissionScope(permissionMap?.['dashboard:manage'])
  if (isNotDefined(scope) || scope.entity === 'self' || scope.entity === 'none') return 'selfOnly'
  if (scope.entity === 'company') return 'company'
  return 'teams'
}

/**
 * Teams the viewer may build a dashboard for. A company-tier viewer can pick any
 * team — they may not be on or manage one themselves — while a teams-tier viewer
 * is limited to the teams they manage. Downline teams aren't in the profile's own
 * membership collections, so the company team list is the reliable name source.
 */
export function getBrowsableDashboardTeams({
  teams,
  managedTeamIds,
  tier,
}: {
  teams: TeamData[]
  managedTeamIds: TeamId[] | undefined
  tier: DashboardManageTier
}): TeamData[] {
  if (tier === 'selfOnly') return []
  if (tier === 'company') return teams

  const managed = new Set<TeamId>(managedTeamIds ?? [])
  return teams.filter((team) => managed.has(team.teamId))
}

/** Audience options offered at each tier, in display order. */
export function getDashboardAudienceKinds(tier: DashboardManageTier): DashboardAudienceKind[] {
  switch (tier) {
    case 'selfOnly':
      return ['yourself']
    case 'teams':
      return ['yourself', 'team']
    case 'company':
      return ['yourself', 'team', 'company', 'role', 'person']
  }
}

/** Whether the kind needs a further choice (which team, which role, which person). */
export function needsAudienceSubSelection(kind: DashboardAudienceKind): boolean {
  switch (kind) {
    case 'yourself':
    case 'company':
      return false
    case 'team':
    case 'role':
    case 'person':
      return true
  }
}

/**
 * The `scopeId` a resolved audience writes, or `undefined` while the audience is
 * still incomplete — which is what disables Continue.
 */
export function resolveDashboardScopeId({
  audience,
  viewerUserId,
  companyId,
}: {
  audience: DashboardAudience | undefined
  viewerUserId: UserId | undefined
  companyId: CompanyId | undefined
}): DashboardScopeId | undefined {
  if (isNotDefined(audience)) return undefined

  switch (audience.kind) {
    case 'yourself':
      return viewerUserId
    case 'company':
      return companyId
    case 'team':
    case 'role':
    case 'person':
      return audience.target?.entityId
  }
}

/**
 * Who the dashboard will reach, for the confirmation banner. `undefined` while
 * the audience is incomplete, which is also when Continue stays disabled.
 */
export function getDashboardAudienceSummary({
  audience,
  companyName,
}: {
  audience: DashboardAudience | undefined
  companyName: string | undefined
}): string | undefined {
  if (isNotDefined(audience)) return undefined

  switch (audience.kind) {
    case 'yourself':
      return 'you'
    case 'company':
      return companyName ?? 'everyone at your company'
    case 'team':
    case 'role':
    case 'person':
      return audience.target?.name
  }
}

/** The audience a scope the list is already filtered to corresponds to. */
export function toDashboardAudience({
  scopeId,
  scopeName,
  viewerUserId,
}: {
  scopeId: DashboardScopeId | undefined
  scopeName: string | undefined
  viewerUserId: UserId | undefined
}): DashboardAudience | undefined {
  if (isNotDefined(scopeId)) return undefined
  if (scopeId === viewerUserId) return { kind: 'yourself' }
  if (isCompanyId(scopeId)) return { kind: 'company' }

  const target: DashboardAudienceTarget = { entityId: scopeId, name: scopeName ?? scopeId }
  if (isTeamId(scopeId)) return { kind: 'team', target }
  if (isRoleId(scopeId)) return { kind: 'role', target }
  return { kind: 'person', target }
}
